import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MessageSquarePlus, Search, Users } from 'lucide-react';
import { useAgentConversations } from '../../../contexts/AgentConversationsContext';
import { patientsService, Patient } from '../../../services/patientsService';
import { relativeWhen } from './conversationPresentation';
import { useOpenPatientPage } from './useOpenPatientPage';

const SEARCH_DEBOUNCE_MS = 250;
const RECENT = 4;
const RESULTS = 6;

const patientName = (p: Patient) => `${p.firstName} ${p.lastName}`.trim() || p.username || `Paciente #${p.id}`;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

type Row = { id: number; name: string; why: string };

// The chat column with no conversation open: first, a conversation about a
// patient (search, or one of the patients talked about lately); then a
// general question.
const ConversationStart: React.FC = () => {
  const agent = useAgentConversations()!;
  const openPatientPage = useOpenPatientPage();
  const searchId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Row[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  // Patients of the newest conversations, once each.
  const recent = useMemo<Row[]>(() => {
    const seen = new Set<number>();
    const rows: Row[] = [];
    for (const c of agent.conversations) {
      if (!c.patient || seen.has(c.patient.id)) continue;
      seen.add(c.patient.id);
      rows.push({
        id: c.patient.id,
        name: c.patient.name,
        why: agent.unread.has(c.id)
          ? 'Respuesta sin leer'
          : c.pendingProposals > 0
            ? c.pendingProposals === 1
              ? '1 propuesta pendiente'
              : `${c.pendingProposals} propuestas pendientes`
            : `Última conversación ${relativeWhen(c.lastMessageAt)}`,
      });
      if (rows.length === RECENT) break;
    }
    return rows;
  }, [agent.conversations, agent.unread]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      return;
    }
    const mine = ++seq.current;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await patientsService.getPatients({ page: 1, search: q });
        if (seq.current !== mine) return;
        setResults(
          (res.results || []).slice(0, RESULTS).map((p) => ({ id: p.id, name: patientName(p), why: p.email })),
        );
      } catch {
        if (seq.current === mine) setResults([]);
      } finally {
        if (seq.current === mine) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const rows = query.trim() ? results : recent;
  const pick = (r: Row) => {
    agent.openForPatient({ id: r.id, name: r.name });
    openPatientPage(r.id);
  };

  return (
    <div className="m-auto grid w-full max-w-[420px] gap-3 px-5 py-6">
      <div className="text-center">
        <h3 className="m-0 text-lg font-semibold text-balance">¿Sobre quién trabajamos?</h3>
        <p className="m-0 mt-1 text-[13px] text-muted-foreground">
          Una conversación sobre un paciente abre su ficha y tiene en cuenta su contexto clínico.
        </p>
      </div>

      <section
        aria-labelledby={`${searchId}-title`}
        className="grid gap-2 rounded-xl border-[1.5px] border-primary bg-card p-3 shadow-[0_0_0_3px_rgb(15_79_77/0.08)]"
      >
        <h4 id={`${searchId}-title`} className="m-0 flex items-center gap-2 text-sm font-semibold">
          <Users className="size-4" aria-hidden="true" /> Conversar sobre un paciente
        </h4>
        <label className="flex h-(--control-height) items-center gap-2 rounded-control border border-input bg-card px-3 text-muted-foreground focus-within:outline-2 focus-within:outline-ring">
          <Search className="size-4 shrink-0" aria-hidden="true" />
          <span className="sr-only">Buscar paciente</span>
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar paciente por nombre o email…"
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground outline-none"
          />
        </label>
        <p className="m-0 mt-0.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
          {query.trim() ? 'Resultados' : 'Recientes'}
        </p>
        {rows.length === 0 ? (
          <p className="m-0 py-1 text-[13px] text-muted-foreground">
            {query.trim()
              ? searching
                ? 'Buscando…'
                : 'Ningún paciente coincide con la búsqueda.'
              : 'Aún no has hablado con Sissi AI sobre ningún paciente. Búscalo arriba.'}
          </p>
        ) : (
          <ul className="m-0 grid list-none gap-0.5 p-0">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => pick(r)}
                  className="box-border flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-0 bg-transparent px-1.5 py-1.5 text-left hover:bg-muted"
                >
                  <span
                    aria-hidden="true"
                    className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                  >
                    {initials(r.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{r.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{r.why}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <button
        type="button"
        onClick={() => agent.startDraft(null)}
        className="box-border flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:bg-muted"
      >
        <MessageSquarePlus className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">Pregunta general</span>
          <span className="block text-xs text-muted-foreground">
            Sin paciente: dudas, plantillas o buscar entre todos tus pacientes.
          </span>
        </span>
      </button>
    </div>
  );
};

export default ConversationStart;
